import {
  Broadcast,
  Phone,
  Television,
  Desktop,
  ShieldCheck,
  WifiHigh,
  ArrowRight,
  CheckCircle,
  Package,
  Tag,
} from "@phosphor-icons/react";
import { Link } from "react-router-dom";

import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { WhatsAppFab } from "../components/WhatsAppFab";
import { ProductSearch } from "../components/ProductSearch";
import { useLang } from "../context/LanguageContext";
import {
  EQUIPMENT_CATEGORIES,
  equipmentStrings,
} from "../lib/equipmentData";
import { useSeo } from "../lib/useSeo";

const ICONS = {
  Broadcast,
  Phone,
  SatelliteDish: Broadcast,
  Television,
  Desktop,
  ShieldCheck,
  WifiHigh,
};

const SEO = {
  pt: {
    title: "Equipamentos · MOTOROLA, INTELBRAS, ALCATEL, DSTV, VSAT — TVCabSat",
    description:
      "Catálogo de equipamentos TVCabSat: rádios MOTOROLA, centrais INTELBRAS e ALCATEL, VSAT, DSTV, computadores, vigilância e redes em Angola.",
  },
  en: {
    title: "Equipment · MOTOROLA, INTELBRAS, ALCATEL, DSTV, VSAT — TVCabSat",
    description:
      "TVCabSat equipment catalog: MOTOROLA radios, INTELBRAS & ALCATEL PBX, VSAT, DSTV, computers, surveillance and networks in Angola.",
  },
  es: {
    title: "Equipos · MOTOROLA, INTELBRAS, ALCATEL, DSTV, VSAT — TVCabSat",
    description:
      "Catálogo de equipos TVCabSat: radios MOTOROLA, centralitas INTELBRAS y ALCATEL, VSAT, DSTV, computadoras, vigilancia y redes en Angola.",
  },
};

const BRANDS_ALL = ["MOTOROLA", "HYTERA", "INTELBRAS", "CISCO", "ALCATEL", "DSTV", "VSAT"];

const Stat = ({ value, label, testId }) => (
  <div
    data-testid={testId}
    className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-7 shadow-sm transition-all hover:border-blue-300 hover:shadow-xl hover:shadow-blue-600/10"
  >
    <div className="pointer-events-none absolute -right-14 -top-14 h-32 w-32 rounded-full bg-blue-500/10 blur-3xl" />
    <div className="font-heading bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-5xl font-black tracking-tighter text-transparent md:text-6xl">
      {value}
    </div>
    <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-blue-600">
      {label}
    </div>
  </div>
);

const CategoryCard = ({ cat, lang, t, reverse }) => {
  const Icon = ICONS[cat.icon] || Package;
  return (
    <article
      id={cat.id}
      data-testid={`equipment-card-${cat.id}`}
      className="hover-lift relative overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm"
    >
      <div
        className={`grid grid-cols-1 lg:grid-cols-2 ${
          reverse ? "lg:[&>div:first-child]:order-2" : ""
        }`}
      >
        <div className="relative min-h-[260px] overflow-hidden lg:min-h-[420px] lg:max-h-[520px]">
          <img
            src={cat.image}
            alt=""
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 hover:scale-105"
          />
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0"
            style={{
              background: `linear-gradient(135deg, ${cat.accent}, transparent 70%)`,
            }}
          />
          {cat.brands.length > 0 && (
            <div className="absolute bottom-4 left-4 flex flex-wrap gap-2">
              {cat.brands.map((b) => (
                <span
                  key={b}
                  className="inline-flex items-center gap-1.5 rounded-full border border-emerald-300/60 bg-emerald-100/90 px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-emerald-800 backdrop-blur shadow-sm"
                >
                  <Tag size={10} weight="fill" />
                  {b}
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="p-8 md:p-10 lg:p-12">
          <div className="icon-tile">
            <Icon size={22} weight="duotone" />
          </div>

          <h3 className="font-heading mt-6 text-3xl font-bold tracking-tight text-slate-900 md:text-4xl">
            {cat.title[lang] || cat.title.pt}
          </h3>
          <p className="mt-3 text-base leading-relaxed text-slate-600 md:text-lg">
            {cat.desc[lang] || cat.desc.pt}
          </p>

          <div className="mt-6 border-t border-slate-100 pt-6">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
              {t.items_title}
            </div>
            <ul className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
              {(cat.items[lang] || cat.items.pt).map((it) => (
                <li
                  key={it}
                  className="flex items-start gap-2 text-sm text-slate-700"
                >
                  <CheckCircle
                    size={15}
                    weight="fill"
                    className="mt-0.5 shrink-0 text-blue-600"
                  />
                  {it}
                </li>
              ))}
            </ul>
          </div>

          <Link
            to="/#contact"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-blue-600 px-5 py-3 text-sm font-semibold text-white shadow-md shadow-blue-600/20 transition-all hover:bg-blue-500 active:scale-95"
          >
            {t.cta_btn}
            <ArrowRight size={14} weight="bold" />
          </Link>
        </div>
      </div>
    </article>
  );
};

export default function Equipment() {
  const { lang } = useLang();
  const t = equipmentStrings[lang] || equipmentStrings.pt;
  useSeo(SEO[lang] || SEO.pt);

  return (
    <div
      data-testid="equipment-page"
      className="relative min-h-screen bg-[#a8c8e2] text-slate-900"
    >
      <Header />

      {/* Hero */}
      <section className="relative isolate overflow-hidden border-b border-slate-200 bg-gradient-to-b from-[#d6e6f2] via-[#b8d6ef] to-[#a8c8e2]">
        <div className="pointer-events-none absolute -top-32 -right-32 -z-10 h-[500px] w-[500px] rounded-full bg-blue-400/20 blur-3xl" />
        <div className="pointer-events-none absolute top-20 -left-40 -z-10 h-[400px] w-[400px] rounded-full bg-purple-400/15 blur-3xl" />
        <div className="absolute inset-0 -z-10 grid-lines opacity-50" />
        <img
          src="/bg-equipment.png"
          alt=""
          aria-hidden
          className="pointer-events-none absolute inset-0 -z-10 h-full w-full object-cover opacity-25 mix-blend-multiply"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 -z-10 bg-gradient-to-r from-[#d6e6f2]/85 via-[#d6e6f2]/60 to-transparent"
        />

        <div className="mx-auto max-w-[1400px] px-6 pb-20 pt-12 md:px-12 md:pt-16 lg:px-16 lg:pb-24 lg:pt-20">
          <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/80 px-3 py-1.5 shadow-sm backdrop-blur-md">
            <Package size={12} weight="bold" className="text-blue-600" />
            <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-slate-600">
              {t.overline}
            </span>
          </div>

          <h1
            data-testid="equipment-title"
            className="font-heading mt-10 max-w-4xl text-3xl font-black leading-[0.95] tracking-tighter text-slate-900 sm:text-4xl md:text-5xl lg:text-6xl"
          >
            {t.title}
          </h1>

          <p
            data-testid="equipment-subtitle"
            className="mt-8 max-w-2xl text-base leading-relaxed text-slate-700 md:text-lg"
          >
            {t.subtitle}
          </p>

          <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-3">
            <Stat
              testId="equipment-stat-categories"
              value={EQUIPMENT_CATEGORIES.length}
              label={t.stat_categories}
            />
            <Stat
              testId="equipment-stat-brands"
              value={BRANDS_ALL.length}
              label={t.stat_brands}
            />
            <Stat
              testId="equipment-stat-warranty"
              value="100%"
              label={t.stat_warranty}
            />
          </div>
        </div>
      </section>

      {/* Product Search */}
      <ProductSearch />

      {/* Brands strip */}
      <section className="border-b border-slate-200 bg-[#dfecf6] py-10">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
          <div className="flex flex-col items-center justify-between gap-6 md:flex-row">
            <div className="font-mono text-[11px] uppercase tracking-[0.28em] text-blue-600">
              {t.brands_title}
            </div>
            <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-4">
              {BRANDS_ALL.map((b) => (
                <span
                  key={b}
                  className="font-heading text-xl font-black tracking-widest text-slate-300 transition-colors hover:text-blue-600 md:text-2xl"
                >
                  {b}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Equipment categories */}
      <section className="py-20 lg:py-24">
        <div className="mx-auto max-w-[1400px] space-y-8 px-6 md:px-12 lg:px-16">
          {EQUIPMENT_CATEGORIES.map((cat, idx) => (
            <CategoryCard
              key={cat.id}
              cat={cat}
              lang={lang}
              t={t}
              reverse={idx % 2 === 1}
            />
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="pb-24 lg:pb-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
          <div className="relative overflow-hidden rounded-3xl border border-slate-200 bg-gradient-to-br from-[#d8e8f5] via-[#d8e8f5] to-blue-50 p-10 shadow-xl md:p-16">
            <div className="pointer-events-none absolute -right-32 -top-32 h-80 w-80 rounded-full bg-blue-400/25 blur-3xl" />
            <div className="relative flex flex-col items-start justify-between gap-6 md:flex-row md:items-center">
              <div>
                <h2 className="font-heading text-4xl font-bold tracking-tight text-slate-900 md:text-5xl">
                  {t.cta_title}
                </h2>
                <p className="mt-3 max-w-xl text-sm leading-relaxed text-slate-600 md:text-base">
                  {t.cta_desc}
                </p>
              </div>
              <Link
                to="/#contact"
                data-testid="equipment-cta"
                className="group inline-flex items-center gap-2 rounded-full bg-blue-600 px-7 py-4 text-sm font-semibold text-white shadow-lg shadow-blue-600/25 transition-all hover:bg-blue-500 hover:shadow-blue-600/40 active:scale-95"
              >
                {t.cta_btn}
                <ArrowRight
                  size={16}
                  weight="bold"
                  className="transition-transform group-hover:translate-x-1"
                />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppFab />
    </div>
  );
}
